import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { AppstoreOutlined, PlusOutlined, ProjectOutlined } from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import ParamListView from '../../components/ParamListView';
import { addCTSubmodule, getCTSubmodules, setCTSubmoduleStatus } from '../../services/testingApi';
import { getSelectedProject } from '../../services/authApi';


const { Text } = Typography;

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Test Cases', href: '/addTestCase' },
  { label: 'Manage Modules', href: '/manageModules' },
  { label: 'Manage Submodules', href: '/test-cases/zinnext/submodules' },
];

export default function ManageSubmodules() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const project = useMemo(() => getSelectedProject(), []);
  const moduleName = searchParams.get('module') ?? '';

  const [submodules, setSubmodules] = useState([]);
  const [submodulesLoading, setSubmodulesLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [submoduleName, setSubmoduleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);

  const loadSubmodules = useCallback(async (projectName, module) => {
    if (!projectName || !module) {
      setSubmodules([]);
      return;
    }
    setSubmodulesLoading(true);
    try {
      const res = await getCTSubmodules({ project: projectName, module });
      setSubmodules(res?.items ?? []);
    } catch (error) {
      console.error(error);
      message.error('Unable to load submodules.');
    } finally {
      setSubmodulesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!project) {
      navigate('/select-project', { replace: true });
      return;
    }
    if (!moduleName) {
      // no module context -> go pick one from Manage Modules
      navigate('/manageModules', { replace: true });
      return;
    }
    Promise.resolve().then(() => loadSubmodules(project.name, moduleName));
  }, [project, moduleName, navigate, loadSubmodules]);

  const handleToggle = async (record, checked) => {
    try {
      await setCTSubmoduleStatus(record.id, checked ? 1 : 0);
      setSubmodules((rows) =>
        rows.map((r) => (r.id === record.id ? { ...r, active: checked ? 1 : 0 } : r)),
      );
      message.success(`Submodule ${checked ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      console.error(error);
      message.error('Unable to update submodule status.');
    }
  };

  const handleAdd = async () => {
    const name = submoduleName.trim();
    if (!name) {
      message.warning('Enter a submodule name.');
      return;
    }
    setSaving(true);
    try {
      await addCTSubmodule({ project: project.name, modulename: moduleName, submodulename: name });
      message.success('Submodule added.');
      setAddOpen(false);
      setSubmoduleName('');
      setListPage(1);
      loadSubmodules(project.name, moduleName);
    } catch (error) {
      console.error(error);
      message.error('Unable to add submodule (maybe it already exists).');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { label: 'Submodule', value: 'submodulename' },
    {
      label: 'Active',
      value: 'active',
      width: 120,
      render: (value, record) => (
        <Switch
          checked={value === 1}
          checkedChildren="On"
          unCheckedChildren="Off"
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
  ];

  if (!project || !moduleName) return null;

  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col>
          <AppBreadcrumb items={breadcrumbItems} />
        </Col>
        <Col>
          <Button type="link" onClick={() => navigate('/manageModules')}>
            Back to Modules
          </Button>
        </Col>
      </Row>

      <div className="zt-page">
        <Card className="v1-card zt-bar">
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col>
              <Space size={16} wrap className="zt-section-title">
                <Space size={6}>
                  <ProjectOutlined />
                  <span>Project:</span>
                  <Text strong>{project.name}</Text>
                </Space>
                <Space size={6}>
                  <AppstoreOutlined />
                  <span>Module:</span>
                  <Text strong>{moduleName}</Text>
                </Space>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddOpen(true)}
              >
                Add Submodule
              </Button>
            </Col>
          </Row>
        </Card>

        <Card
          className="v1-card zt-fall"
          title={(
            <Space size={6} className="zt-section-title">
              <AppstoreOutlined />
              <span>Submodules</span>
            </Space>
          )}
        >
          <ParamListView
            listName="Submodules"
            loading={submodulesLoading}
            fields={fields}
            dataSource={submodules}
            current={listPage}
            pageSize={listPageSize}
            total={submodules.length}
            onPageChange={setListPage}
            onPageSizeChange={(size) => {
              setListPage(1);
              setListPageSize(size);
            }}
            rowSelection={false}
            className="zt-list-plain"
          />
        </Card>
      </div>

      <Modal
        title="Add Submodule"
        open={addOpen}
        confirmLoading={saving}
        okText="Add"
        onOk={handleAdd}
        onCancel={() => { setAddOpen(false); setSubmoduleName(''); }}
        destroyOnHidden
      >
        <Space direction="vertical" className="zt-modal-stack">
          <Text type="secondary">Project: <b>{project.name}</b> &nbsp;|&nbsp; Module: <b>{moduleName}</b></Text>
          <Input
            placeholder="Enter submodule name"
            maxLength={100}
            value={submoduleName}
            onChange={(e) => setSubmoduleName(e.target.value)}
            onPressEnter={handleAdd}
          />
        </Space>
      </Modal>
    </main>
  );
}
